import React, {
  ChangeEvent,
  FC,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from "react";
import Button from "components/Button/Button";
import Select from "components/Select/Select";
import {
  Content,
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { useAgentJobRun } from "hooks/useAgentJobRun";
import useFetchOnceEffect from "hooks/useFetchOnceEffect";
import { Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { ActionMeta } from "react-select";
import {
  addInboundShipment,
  getInboundAddresses,
} from "reduxFolder/reducers/inbound";
import {
  closeRightPanel,
  openRightPanel,
} from "reduxFolder/reducers/rightPanel";
import {
  inboundSelectAddresses,
  inboundSelectAddressesLoaded,
  inboundSelectAddressesLoading,
} from "reduxFolder/selectors/inboundSelectors";
import { createStructuredSelector } from "reselect";
import { FieldWrapper } from "styles/formStyles";
import { InboundShipmentDetailDto } from "types/api/inbound";
import { InboundShipmentParseJobDto } from "types/api/inboundShipmentParseJobs";
import { OpenRightPanel } from "types/common/rightPanel";
import { pollAgentJob, readAgentJob } from "utils/agentJob";
import {
  AgentEvent,
  AgentPhase,
  initialAgentPhase,
  reduceAgentPhase,
} from "utils/agentJobPhase";
import api from "utils/api";
import {
  toastResponseError,
  toastResponseSuccess,
} from "utils/responseMessageHelper";
import {
  buildInboundAddressOptions,
  InboundAddressOption,
} from "../InboundShipmentPanel/inboundShipmentFormHelper";
import {
  ButtonsWrapper,
  Description,
  ErrorList,
  Hint,
  Notes,
  StatusBlock,
} from "./InboundParsePanel.styles";

type Phase = AgentPhase<InboundShipmentParseJobDto>;
type Event = AgentEvent<InboundShipmentParseJobDto>;
const TERMINAL_STATUSES = ["completed", "failed"] as const;

const reduce = (phase: Phase, event: Event): Phase =>
  reduceAgentPhase(phase, event);

// Free-form package notes (INBOUND_PARSE): the customer pastes one paragraph per
// package, picks the ship-from address, and the backend job creates the draft
// inbound shipment. On success the shipment panel opens exactly as CSV import
// does; on failure the importer's "Row N: ..." messages are listed for editing.
interface InboundParsePanelProps {
  panelData: Map<string, any>;
  isOpen: boolean;
  addresses: any;
  isAddressesLoading: boolean;
  addressesLoaded: boolean;
  getInboundAddresses: () => Promise<any>;
  addInboundShipment: (shipment: InboundShipmentDetailDto) => void;
  openRightPanel: OpenRightPanel;
  closeRightPanel: () => void;
}

export const InboundParsePanel: FC<InboundParsePanelProps> = ({
  isOpen,
  addresses,
  isAddressesLoading,
  addressesLoaded,
  getInboundAddresses,
  addInboundShipment,
  openRightPanel,
  closeRightPanel,
}) => {
  const { t } = useTranslation("common");
  const [phase, dispatch] = useReducer(reduce, initialAgentPhase as Phase);
  const [rawInput, setRawInput] = useState("");
  const [address, setAddress] = useState<InboundAddressOption | null>(null);

  useFetchOnceEffect(!addressesLoaded, () =>
    getInboundAddresses().catch(toastResponseError),
  );
  const addressOptions = useMemo(
    () => buildInboundAddressOptions(addresses),
    [addresses],
  );

  const finish = useCallback(
    (job: InboundShipmentParseJobDto) => {
      dispatch({ type: "JOB_DONE", result: job });
      toastResponseSuccess(t("inbound.parse.success"));
      if (job.inbound_shipment) {
        addInboundShipment(job.inbound_shipment);
        openRightPanel("INBOUND_SHIPMENT", Map({ id: job.inbound_shipment.id }));
      } else {
        closeRightPanel();
      }
    },
    [addInboundShipment, openRightPanel, closeRightPanel, t],
  );

  // Closing the panel aborts the poll and drops any result that lands late.
  const { start, keepWaiting } = useAgentJobRun<
    InboundShipmentParseJobDto,
    "completed" | "failed"
  >({
    poll: (jobId, signal) =>
      pollAgentJob(jobId, {
        jobsApi: api.inboundShipmentParseJobs,
        terminalStatuses: TERMINAL_STATUSES,
        signal,
      }),
    isActive: isOpen,
    dispatch,
    onDone: finish,
  });

  const canSubmit = rawInput.trim() !== "" && address !== null;

  const handleSubmit = useCallback(() => {
    if (phase.name !== "input" || !canSubmit || !address) return;
    dispatch({ type: "SUBMIT", canSubmit: true });
    start(() =>
      api.inboundShipmentParseJobs
        .create({ raw_input: rawInput.trim(), inbound_address_id: address.value })
        .then((response) => readAgentJob<InboundShipmentParseJobDto>(response)),
    );
  }, [phase.name, canSubmit, address, rawInput, start]);

  const handleKeepWaiting = useCallback(() => {
    if (phase.name !== "timeout") return;
    keepWaiting(phase.jobId);
  }, [phase, keepWaiting]);

  const handleChangeSelect = useCallback(
    (selectedOption: unknown, _context: ActionMeta<unknown>) => {
      setAddress(selectedOption as InboundAddressOption | null);
    },
    [],
  );

  const cancelButton = (
    <Button type={ButtonType.Button} onClick={closeRightPanel}>
      {t("common.cancel")}
    </Button>
  );

  return (
    <ContentWrapper>
      <Heading>{t("inbound.parse.title")}</Heading>
      <Content>
        <div>
          {phase.name === "input" && (
            <>
              <Description>{t("inbound.parse.description")}</Description>
              <FieldWrapper>
                <Select
                  placeholder={t("inbound.form.address")}
                  name="address"
                  value={address}
                  onChange={handleChangeSelect}
                  options={addressOptions}
                  isLoading={isAddressesLoading}
                  disabled={false}
                />
              </FieldWrapper>
              <Notes
                name="raw_input"
                aria-label={t("inbound.parse.title")}
                placeholder={t("inbound.parse.placeholder")}
                value={rawInput}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  setRawInput(event.target.value)
                }
              />
              <ButtonsWrapper>
                {cancelButton}
                <Button
                  type={ButtonType.Button}
                  color={ButtonColor.Primary}
                  variant={ButtonVariant.Filled}
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  {t("inbound.parse.submit")}
                </Button>
              </ButtonsWrapper>
            </>
          )}

          {phase.name === "parsing" && (
            <StatusBlock role="status" aria-live="polite">
              <p>{t("inbound.parse.parsing")}</p>
              <Hint>{t("inbound.parse.parsingHint")}</Hint>
              <ButtonsWrapper>{cancelButton}</ButtonsWrapper>
            </StatusBlock>
          )}

          {phase.name === "timeout" && (
            <StatusBlock role="status" aria-live="polite">
              <p>{t("inbound.parse.timeout")}</p>
              <ButtonsWrapper>
                {cancelButton}
                <Button
                  type={ButtonType.Button}
                  color={ButtonColor.Primary}
                  variant={ButtonVariant.Filled}
                  onClick={handleKeepWaiting}
                >
                  {t("inbound.parse.keepWaiting")}
                </Button>
              </ButtonsWrapper>
            </StatusBlock>
          )}

          {phase.name === "failed" && (
            <StatusBlock role="alert">
              <p>
                {phase.failure.kind === "unavailable"
                  ? t("inbound.parse.unavailable")
                  : t("inbound.parse.failedTitle")}
              </p>
              {phase.failure.kind === "validation" && (
                <>
                  <Hint>{t("inbound.parse.failedValidation")}</Hint>
                  <ErrorList>
                    {phase.failure.fieldErrors
                      .flatMap((fieldError) => fieldError.messages)
                      .map((message) => <li key={message}>{message}</li>)}
                  </ErrorList>
                </>
              )}
              {phase.failure.kind === "generic" && phase.failure.detail && (
                <Hint>{phase.failure.detail}</Hint>
              )}
              <ButtonsWrapper>
                {cancelButton}
                {phase.failure.kind !== "unavailable" && (
                  <Button
                    type={ButtonType.Button}
                    color={ButtonColor.Primary}
                    variant={ButtonVariant.Filled}
                    onClick={() => dispatch({ type: "RETRY" })}
                  >
                    {t("inbound.parse.tryAgain")}
                  </Button>
                )}
              </ButtonsWrapper>
            </StatusBlock>
          )}
        </div>
      </Content>
    </ContentWrapper>
  );
};

const withConnect = connect(
  createStructuredSelector({
    addresses: inboundSelectAddresses,
    isAddressesLoading: inboundSelectAddressesLoading,
    addressesLoaded: inboundSelectAddressesLoaded,
  }),
  {
    getInboundAddresses,
    addInboundShipment,
    openRightPanel,
    closeRightPanel,
  },
);

export default withConnect(InboundParsePanel);
